import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../components/layout/Sidebar";
import {
  ArrowLeft,
  Globe,
  Users,
  FileSignature,
  BarChart3,
  ShoppingBag,
  TrendingUp,
  Target,
  Award,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
} from "lucide-react";

const Consultancy = () => {
  type TabId = "overview" | "services" | "process" | "benefits" | "faq" | "portfolio";
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const pageName = "Consultancy & Agency Representation";
  const title = `${pageName} | Northstart Systems`;
  const description =
    "We help global ICT brands succeed in Kenya through market entry strategy, partner representation, and channel development.";
  const canonical = "https://northstart.co.ke/services/consultancy";

  const heroBadges = [
    { label: "Market Experts", icon: <Globe className="w-4 h-4 mr-2" /> },
    { label: "Partnership Network", icon: <Users className="w-4 h-4 mr-2" /> },
    { label: "Proven Negotiators", icon: <FileSignature className="w-4 h-4 mr-2" /> },
    { label: "Growth Driven", icon: <TrendingUp className="w-4 h-4 mr-2" /> },
  ];

  const services = [
    {
      icon: <BarChart3 className="w-8 h-8 text-amber-400" />,
      title: "Market Analysis",
      desc: "Comprehensive research on Kenya’s ICT landscape, competition, and customer needs.",
      bullets: [
        "Competitor benchmarking",
        "Industry trend reporting",
        "Regulatory environment mapping",
        "Customer segmentation",
      ],
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      title: "Partner Identification",
      desc: "Connecting brands with reliable distributors, resellers, and system integrators.",
      bullets: [
        "Shortlisting verified partners",
        "Due diligence and screening",
        "Introductions and matchmaking",
        "Long-term collaboration roadmaps",
      ],
    },
    {
      icon: <FileSignature className="w-8 h-8 text-orange-400" />,
      title: "Contract Negotiation",
      desc: "Local representation to negotiate fair, win-win contracts with partners and clients.",
      bullets: [
        "Legal & compliance alignment",
        "Pricing and SLA negotiations",
        "Risk-sharing frameworks",
        "Dispute resolution mechanisms",
      ],
    },
    {
      icon: <ShoppingBag className="w-8 h-8 text-green-400" />,
      title: "Sales Channel Development",
      desc: "Building effective distribution and sales channels for ICT products.",
      bullets: [
        "Channel partner recruitment",
        "Sales pipeline building",
        "Marketing alignment",
        "Performance monitoring",
      ],
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-pink-400" />,
      title: "Market Expansion Strategy",
      desc: "Guiding brands through structured expansion across East Africa.",
      bullets: [
        "Regional opportunity analysis",
        "Go-to-market strategy",
        "Localization of brand messaging",
        "Phased expansion planning",
      ],
    },
  ];

  const process = [
    { step: "Discovery", points: ["Understand brand objectives", "Align on target markets", "Identify key KPIs"] },
    { step: "Research", points: ["Market scans & competitor analysis", "Regulatory requirements", "Risk assessment"] },
    { step: "Representation", points: ["Local brand representation", "Partner engagements", "Negotiations & contracts"] },
    { step: "Growth", points: ["Sales channel setup", "Marketing campaigns", "Continuous reporting & feedback"] },
  ];

  const benefits = [
    {
      icon: <Target className="w-8 h-8 text-emerald-400" />,
      title: "Local Knowledge",
      desc: "Deep understanding of Kenyan ICT sector to navigate regulations and opportunities.",
    },
    {
      icon: <Award className="w-8 h-8 text-blue-400" />,
      title: "Proven Track Record",
      desc: "Years of experience helping ICT brands establish and grow in East Africa.",
    },
    {
      icon: <Globe className="w-8 h-8 text-purple-400" />,
      title: "Regional Reach",
      desc: "Beyond Kenya—we support expansion across East African markets.",
    },
  ];

  const faqItems = [
    { q: "Do you only work with ICT brands?", a: "Yes, our specialization is ICT-focused market entry and representation." },
    { q: "Can you represent us long-term?", a: "We offer both project-based consultancy and ongoing representation models." },
    { q: "Do you provide marketing support?", a: "Absolutely. We help with localized campaigns, events, and digital outreach." },
    { q: "Which regions do you cover?", a: "Our primary base is Kenya, with reach into East African markets." },
  ];

  const gallery = ["/images/agency/1.jpeg", "/images/agency/2.jpeg", "/images/agency/3.jpeg"];

  const tabs: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "services", label: "Services" },
    { id: "process", label: "Process" },
    { id: "benefits", label: "Benefits" },
    { id: "faq", label: "FAQ" },
    { id: "portfolio", label: "Portfolio" },
  ];

  const servicesJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Northstart Systems",
    url: "https://northstart.co.ke",
    description,
    areaServed: "Kenya",
    telephone: "+254700000000",
    makesOffer: services.map((s) => ({ "@type": "Service", name: s.title })),
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-20 bg-gradient-to-br from-slate-200 via-blue-900 to-indigo-200 min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="ICT agency representation Kenya, ICT consultancy Nairobi, technology market entry, ICT partnerships Africa, sales channel development Kenya"
        />
        <meta property="og:title" content={pageName + " | Northstart Systems"} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/agency/1.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageName + " | Northstart Systems"} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/agency/1.jpeg" />
        <script type="application/ld+json">{JSON.stringify(servicesJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 flex gap-8">
        <Sidebar />
        <main className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-b border-white/20">
            <Link to="/services" className="inline-flex items-center text-cyan-300 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Services
            </Link>
            <h1 className="text-4xl font-bold mb-3">{pageName}</h1>
            <p className="text-lg text-slate-200 mb-6">{description}</p>
            <div className="flex flex-wrap gap-3">
              {heroBadges.map((b, i) => (
                <div key={i} className="flex items-center bg-white/10 px-3 py-2 rounded-full border border-white/20">
                  {b.icon}
                  <span className="text-slate-100 text-sm">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 py-3 bg-white/5 border-b border-white/10 flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === t.id
                    ? "bg-amber-500/30 text-white border border-amber-400/50"
                    : "bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-8 text-white">
            {activeTab === "overview" && (
              <section className="space-y-6">
                <h2 className="text-3xl font-bold">Strategic ICT Representation in Kenya</h2>
                <p className="text-slate-300">
                  We bridge global ICT brands and the Kenyan market. From initial research to channel development,
                  Northstart ensures your entry is smooth, compliant, and profitable.
                </p>
              </section>
            )}

            {activeTab === "services" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Our Consultancy Services</h2>
                <div className="grid gap-8">
                  {services.map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-6">
                        <div className="p-4 bg-white/10 rounded-2xl">{s.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-2">{s.title}</h3>
                          <p className="text-slate-300 mb-4">{s.desc}</p>
                          <ul className="grid md:grid-cols-2 gap-2">
                            {s.bullets.map((b, idx) => (
                              <li key={idx} className="flex items-start">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                                <span className="text-slate-200 text-sm">{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "process" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Our Engagement Process</h2>
                <div className="grid md:grid-cols-4 gap-6">
                  {process.map((p, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6 text-center">
                      <div className="text-2xl font-bold text-amber-400 mb-3">{i + 1}</div>
                      <h3 className="font-semibold mb-2">{p.step}</h3>
                      <ul className="text-sm text-slate-300 space-y-1">
                        {p.points.map((pt, idx) => (
                          <li key={idx}>{pt}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "benefits" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Why Work With Us</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {benefits.map((b, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      {b.icon}
                      <h3 className="text-xl font-semibold mt-4 mb-2">{b.title}</h3>
                      <p className="text-slate-300 text-sm">{b.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "faq" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {faqItems.map((f, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      <h3 className="text-lg font-semibold mb-2">{f.q}</h3>
                      <p className="text-slate-300 text-sm">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "portfolio" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Agency Representation Highlights</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {gallery.map((img, idx) => (
                    <div key={idx} className="group relative overflow-hidden rounded-2xl">
                      <img
                        src={img}
                        alt={`${pageName} ${idx + 1}`}
                        className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1">Representation {idx + 1}</h3>
                          <p className="text-slate-200 text-sm">ICT market entry support</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact Strip */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6 grid sm:grid-cols-3 gap-6 text-center">
                  <div className="flex flex-col items-center">
                    <Phone className="w-7 h-7 text-cyan-400 mb-2" />
                    <p className="text-slate-200">+254 700 041385</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <Mail className="w-7 h-7 text-emerald-400 mb-2" />
                    <p className="text-slate-200">info@northstartsystems.com</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <MapPin className="w-7 h-7 text-purple-400 mb-2" />
                    <p className="text-slate-200">Riara Valley Garden, Apartment C8, 3rd Floor, Off Riara Road Kilimani, Nairobi, Kenya</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Consultancy;
